/**
 * **다 친 전화번호를 보이는 한 곳** — 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지 — 단위 시험이 node 에서 그대로 읽는다).
 *
 * 다 친 번호는 자릿수까지 알므로 **번호 종류**로 끊는다([telText]). 치는 중인 번호는 접두의 종류대로 앞에서부터 끊다가([telTyping]),
 * 다 친 번호가 되면 [telText] 와 같은 모양이 된다 — 옛 입력 중 서식(`inputTel2`)은 서울 번호의 가운데를 늘 4자리로 자르고
 * (9자리 `023067788` → `02-3067-788`) 지역번호·050·대표번호를 끊지 못해 지웠다(2026-10-30).
 *
 * 저장은 **숫자만**이다(2026-10-30 사용자 결정 「전화번호도 사업자번호와 같은 개념으로」 — 서버 `CustomerPhone.storageForm` 이 알아보는
 * 번호를 숫자만으로 저장하고, 기존 값은 정리 SQL 이 맞췄다). 입력 칸은 [telFieldText] 로 보이고 [telDigits] 로 보낸다.
 *
 * 번호 종류는 앱서버 `CustomerPhone.kindOf`(고객 기록·고객 가져오기·POS 고객 검색이 같이 쓰는 표 — 시안 `telKind`)와 **같은 표**다.
 * 표가 갈리면 서버가 알아본 번호를 화면이 끊지 못한다 — 단위 시험이 서버 시험의 진리표를 그대로 대조한다.
 * 알아보지 못한 번호(뒷 4자리·빈 값·그 밖)는 받은 글자 그대로 둔다 — 모르는 번호를 그럴듯하게 끊지 않는다.
 */

/** 번호 종류 — 앱서버 `CustomerPhone.PhoneKind` 와 같은 글자(가져오기 Person 의 `kind` 가 이 글자다) */
export const TEL_KIND = Object.freeze({
  MOBILE: 'MOBILE',
  OLD_MOBILE: 'OLD_MOBILE',
  LANDLINE: 'LANDLINE',
  VOIP: 'VOIP',
  SAFE: 'SAFE',
  REPRESENTATIVE: 'REPRESENTATIVE',
  LAST4: 'LAST4',
  NONE: 'NONE',
  INVALID: 'INVALID',
})

/** 지역번호 — 실제 접두만(서울 `02` 는 따로 본다). 목록 밖(`034`·`035`·`045`·`056`·`065` 등)은 유선이 아니다 */
const AREA_CODES = Object.freeze([
  '031',
  '032',
  '033',
  '041',
  '042',
  '043',
  '044',
  '051',
  '052',
  '053',
  '054',
  '055',
  '061',
  '062',
  '063',
  '064',
])

/**
 * 종류마다 접두와 자릿수 — 휴대폰 `010`+8 · 옛 휴대폰 `011`·`016`~`019`+7~8 · 유선 `02`·지역번호+7~8 · 인터넷전화 `070`+8 ·
 * 안심번호 `050`+8~9(`050-XXXX-XXXX`·`050X-XXXX-XXXX`) · 대표번호 `1[5-9]`+6(`15xx`·`16xx`·`18xx`…). 서버와 같은 순서로 본다.
 */
const KIND_PATTERNS = Object.freeze([
  [TEL_KIND.MOBILE, /^010[0-9]{8}$/],
  [TEL_KIND.OLD_MOBILE, /^01[16789][0-9]{7,8}$/],
  [TEL_KIND.LANDLINE, new RegExp(`^(?:02|${AREA_CODES.join('|')})[0-9]{7,8}$`)],
  [TEL_KIND.VOIP, /^070[0-9]{8}$/],
  [TEL_KIND.SAFE, /^050[0-9]{8,9}$/],
  [TEL_KIND.REPRESENTATIVE, /^1[5-9][0-9]{6}$/],
])

/**
 * 숫자만 남긴 번호의 종류 — 앱서버 `CustomerPhone.kindOf` 와 같은 답이다. 숫자 아닌 글자가 섞였으면 `INVALID`, 빈 값은 `NONE`,
 * 4자리는 `LAST4`.
 *
 * @param {?string} digits 숫자만 남긴 번호
 * @returns {string} [TEL_KIND] 의 하나
 */
export function telKindOf(digits) {
  const text = digits == null ? '' : String(digits)
  if (!/^[0-9]*$/.test(text)) return TEL_KIND.INVALID
  if (text === '') return TEL_KIND.NONE
  if (text.length === 4) return TEL_KIND.LAST4

  const found = KIND_PATTERNS.find(([, pattern]) => pattern.test(text))
  return found ? found[0] : TEL_KIND.INVALID
}

/**
 * **번호의 숫자만** — 하이픈·공백·괄호를 걷는다. 빈 값은 빈 글자. 같은 번호인지는 이 값으로 견준다(표기가 달라도 같은 번호다).
 *
 * @param {?string} tel
 * @returns {string}
 */
export function telDigits(tel) {
  return String(tel == null ? '' : tel).replace(/[^0-9]/g, '')
}

/** 휴대폰 종류 — 앱서버 `CustomerPhone.MOBILE_KINDS` 와 같은 둘 */
const MOBILE_KINDS = Object.freeze([TEL_KIND.MOBILE, TEL_KIND.OLD_MOBILE])

/**
 * **휴대폰 번호인가** — 하이픈·공백이 섞여 와도 숫자만 남겨 종류로 본다(앱서버 `EquipmentRequestRules.isMobile` 과 같은 답). 알림톡은
 * 휴대폰에만 닿는다.
 *
 * @param {?string} tel
 * @returns {boolean}
 */
export function isMobileTel(tel) {
  return MOBILE_KINDS.indexOf(telKindOf(telDigits(tel))) > -1
}

/** 머리 · 가운데 · 끝 4자리 */
const grouped = (digits, head) => [digits.slice(0, head), digits.slice(head, -4), digits.slice(-4)].join('-')

/** 표기를 판정할 때 걷는 구분 글자 — 하이픈·공백·괄호·점(앱서버 `CustomerPhone.storageForm` 과 같은 글자). 그 밖의 글자가 섞이면 원문 그대로 */
const SEPARATORS = /[-\s().]/g

/**
 * **다 친 번호의 표기** — 구분 글자(하이픈·공백·괄호·점)가 섞여 와도 걷어 내고 종류로 끊는다: 서울 `02-XXX(X)-XXXX` · 지역번호·
 * 휴대폰·070 `0XX-XXX(X)-XXXX` · 안심번호 `050X-XXX(X)-XXXX`(050 뒤 한 자리는 통신사 식별 — 11자리 `0507-123-4567`, 2026-10-30
 * 사용자 결정) · 대표번호 `1588-XXXX`. 알아보지 못한 번호와 다른 글자(`+`·영문·한글)가 섞인 값은 받은 글자 그대로다 — 오타를
 * 정상 번호처럼 보이지 않는다. 앱서버 `CustomerPhone.masked` 가 같은 자리에서 끊는다.
 *
 * @param {?string} tel
 * @returns {string}
 */
export function telText(tel) {
  const digits = String(tel == null ? '' : tel).replace(SEPARATORS, '')

  switch (telKindOf(digits)) {
    case TEL_KIND.MOBILE:
    case TEL_KIND.OLD_MOBILE:
    case TEL_KIND.VOIP:
      return grouped(digits, 3)
    case TEL_KIND.LANDLINE:
      return grouped(digits, digits.startsWith('02') ? 2 : 3)
    case TEL_KIND.SAFE:
      return grouped(digits, 4)
    case TEL_KIND.REPRESENTATIVE:
      return `${digits.slice(0, 4)}-${digits.slice(4)}`
    default:
      return String(tel == null ? '' : tel).trim()
  }
}

/**
 * 치는 중인 번호의 묶음 — 접두로 종류가 정해지면 머리·가운데 자릿수. 가운데는 그 종류의 **짧은 쪽**으로 끊다가, 다 친 번호가 되면
 * [telText] 가 자릿수에 맞춰 다시 끊는다(서울 9자리 `02-123-4567` → 10자리 `02-1234-5678`, 지역번호·옛 휴대폰 10 → 11자리,
 * 안심번호 11자리 `0507-123-4567` → 12자리 `0507-1234-5678`). 접두·자릿수는 [KIND_PATTERNS] 와 같은 표다 — 시험이 「다 친 번호는
 * [telText] 와 같은 답」으로 묶는다.
 */
const TYPING_SHAPES = Object.freeze([
  [/^02/, 2, 3],
  [/^050/, 4, 3],
  [/^0(?:10|70)/, 3, 4],
  [/^01[16789]/, 3, 3],
  [new RegExp(`^(?:${AREA_CODES.join('|')})`), 3, 3],
  [/^1[5-9]/, 4, 0],
])

/**
 * **치는 중인 번호의 표기** — 하이픈·공백이 섞여 와도 숫자만 남겨, 다 친 번호면 [telText] 그대로, 치는 중이면 접두의 종류대로 앞에서부터
 * 끊는다(`010-1`·`02-306`·`031-123-4`·`1588-1`). 종류를 아직 모르는 접두(`0`·`01`·`05`), 표에 없는 접두(`034`·`080`·`012`·`0` 아닌
 * 숫자로 시작하는 번호), 그 종류의 자릿수를 넘친 번호는 숫자만 그대로 둔다 — 모르는 번호를 그럴듯하게 끊지 않는다.
 * 입력 칸은 [telFieldText] 로 이 모양을 보이고, 보내는 값은 [telDigits] 로 숫자만이다.
 *
 * @param {?string} value
 * @returns {string}
 */
export function telTyping(value) {
  const digits = telDigits(value)
  const complete = telText(digits)
  if (complete !== digits) return complete

  const shape = TYPING_SHAPES.find(([prefix]) => prefix.test(digits))
  if (!shape) return digits
  const [, head, middle] = shape
  if (digits.length > head + middle + 4) return digits

  return [digits.slice(0, head), digits.slice(head, head + middle), digits.slice(head + middle)]
    .filter(Boolean)
    .join('-')
}

/**
 * **입력 칸에 보일 모양** — 숫자만인 값은 치는 대로 끊어 보이고([telTyping]), 저장된 옛 값처럼 숫자만이 아닌 값(자리표시
 * `00-0000-0000`·`+82 10-…`)은 받은 그대로 보인다. 칸은 `:value="util.telFieldText(v)" @input="v = util.telDigits($event)"` —
 * 서버에는 숫자만 가고, 사람이 친 `-` 는 버린다. 그 칸을 고치지 않으면 저장 값도 그대로다.
 *
 * @param {?string} value
 * @returns {string}
 */
export function telFieldText(value) {
  const text = value == null ? '' : String(value)
  return /^[0-9]*$/.test(text) ? telTyping(text) : text
}
